// @core/stores/search/SearchStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { searchApi } from '@core/api';
import type {
  BulkIndexDocumentsRequest,
  BulkIndexDocumentsResponse,
  CreateSearchIndexRequest,
  FederatedSearchRequest,
  GetIndexStatsResponse,
  IndexDocumentRequest,
  IndexDocumentResponse,
  ReIndexRequest,
  ReIndexResponse,
  SearchIndex,
  SearchRequest,
  SearchResponse,
  SuggestResponse,
  UpdateSearchIndexRequest,
  ValidateSearchPolicyRequest,
  ValidateSearchPolicyResponse,
} from '@core/types/search';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SearchStore {
  // List state
  items: SearchIndex[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: SearchIndex | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, SearchIndex>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): SearchIndex | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: SearchIndex | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Bulk index documents
   */
  async BulkIndexDocuments(data: BulkIndexDocumentsRequest): Promise<BulkIndexDocumentsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.BulkIndexDocuments(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create search index
   */
  async CreateSearchIndex(data: CreateSearchIndexRequest): Promise<SearchIndex> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await searchApi.CreateSearchIndex(data);
      const item = response.index;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete indexed document
   */
  async DeleteDocument(entity_urn: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await searchApi.DeleteDocument(entity_urn);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== entity_urn);

        // Clear current item if same
        if (this.currentItem?.entity?.id === entity_urn) {
          this.currentItem = null;
        }

        this.removeFromCache(entity_urn);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete search index
   */
  async DeleteSearchIndex(index_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await searchApi.DeleteSearchIndex(index_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== index_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === index_id) {
          this.currentItem = null;
        }

        this.removeFromCache(index_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Federated search across peers
   */
  async FederatedSearch(data: FederatedSearchRequest): Promise<SearchResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.FederatedSearch(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get index statistics
   */
  async GetIndexStats(index_id: string): Promise<GetIndexStatsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.GetIndexStats(index_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get search index
   */
  async GetSearchIndex(index_id: string): Promise<SearchIndex> {
    // Check cache first
    const cached = this.cache.get(index_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await searchApi.GetSearchIndex(index_id);
      const item = response.index;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Index a document
   */
  async IndexDocument(data: IndexDocumentRequest): Promise<IndexDocumentResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.IndexDocument(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List search indexes
   */
  async ListSearchIndexes(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await searchApi.ListSearchIndexes(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Trigger reindex
   */
  async ReIndex(index_id: string, data: ReIndexRequest): Promise<ReIndexResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.ReIndex(index_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search across products
   */
  async Search(data: SearchRequest): Promise<SearchResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.Search(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get search suggestions
   */
  async Suggest(params?: { limit?: string | number | boolean; q?: string | number | boolean; }): Promise<SuggestResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.Suggest(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update search index
   */
  async UpdateSearchIndex(index_id: string, data: UpdateSearchIndexRequest): Promise<SearchIndex> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await searchApi.UpdateSearchIndex(index_id, data);
      const item = response.index;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Validate search policy
   */
  async ValidateSearchPolicy(data: ValidateSearchPolicyRequest): Promise<ValidateSearchPolicyResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await searchApi.ValidateSearchPolicy(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const searchStore = new SearchStore();
